import { useMemo, useState } from "react";
import { ExternalLink, Github } from "lucide-react";
import { CATEGORIES, PROJECTS } from "@/data/portfolio";
import { useReveal } from "@/hooks/useReveal";

export default function PortfolioGrid() {
  const [filter, setFilter] = useState("all");
  const { ref, visible } = useReveal();

  const items = useMemo(() => {
    if (filter === "all") return PROJECTS;
    return PROJECTS.filter((p) => p.category === filter);
  }, [filter]);

  return (
    <section id="portfolio" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10" ref={ref}>
        <div className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 fade-in-up ${visible ? "in" : ""}`}>
          <div>
            <div className="section-label mb-4">Selected Work</div>
            <h2 className="heading-hero text-4xl md:text-5xl lg:text-6xl">
              Recent <span className="text-[#FF3B30]">projects.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md">
            A curated selection of homestays, hotels, cafés, schools, e-commerce and business websites.
          </p>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filter-bar">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={`filter-${c.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                filter === c.key
                  ? "bg-[#FF3B30] text-white border-[#FF3B30] shadow-lg shadow-red-500/30"
                  : "bg-white/[0.03] text-white/70 border-white/10 hover:border-[#FF3B30]/40 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <article
              key={p.id}
              className="glass-card rounded-3xl overflow-hidden group flex flex-col"
              data-testid={`project-card-${p.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-white/5">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <span className="absolute top-4 left-4 badge-tech !bg-black/70 !text-white/90 uppercase tracking-widest !text-[10px]">
                  {CATEGORIES.find((c) => c.key === p.category)?.label || p.category}
                </span>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-display font-bold text-xl leading-tight mb-2">{p.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed mb-4 flex-1">{p.desc}</p>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {p.tech.map((t) => (
                    <span key={t} className="badge-tech">{t}</span>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <a
                    href={p.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-semibold hover:bg-[#ff5147] transition-colors"
                    data-testid={`project-live-${p.id}`}
                  >
                    <ExternalLink size={14} /> Live Demo
                  </a>
                  <a
                    href={p.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-white/5 border border-white/10 text-white text-sm font-semibold hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/5 transition-colors"
                    data-testid={`project-github-${p.id}`}
                  >
                    <Github size={14} /> GitHub
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        {items.length === 0 && (
          <p className="text-center text-white/50 mt-12">No projects in this category yet.</p>
        )}
      </div>
    </section>
  );
}
